import { useCallback, useState } from 'react';
import { useMutation, useApolloClient } from '@apollo/client/react';
import { useNavigate } from 'react-router-dom';
import {
  LOGIN_MUTATION,
  LOGOUT_MUTATION,
} from '../apollo/operations/auth.operations';

const ACCESS_TOKEN_KEY  = 'bms_access_token';
const REFRESH_TOKEN_KEY = 'bms_refresh_token';

// ─────────────────────────────────────────────
// JWT payload decode (client-side only — NOT verification)
// ─────────────────────────────────────────────
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1];
    // Pad base64url to standard base64
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(
      payload.length + ((4 - (payload.length % 4)) % 4), '='
    );
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

// ─────────────────────────────────────────────
// Token expiry check — uses the JWT's standard `exp` claim (outer JWT always
// has it even though the inner payload is AES-encrypted).
// ─────────────────────────────────────────────
export function isTokenExpired(): boolean {
  const token = localStorage.getItem('bms_access_token');
  if (!token) return true;
  const decoded = decodeJwtPayload(token);
  if (!decoded?.exp) return true;
  return (decoded.exp as number) < Date.now() / 1000;
}

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────
export interface AuthUser {
  employeeId: string;
  username: string;
  ispcode: string;
}

export interface UseAuthReturn {
  isAuthenticated: boolean;
  currentUser: AuthUser | null;
  login: (username: string, password: string, tenantCode: string) => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => string | null;
}

// ─────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────
export function useAuth(): UseAuthReturn {
  const navigate   = useNavigate();
  const apolloClient = useApolloClient();

  // Derive isAuthenticated from localStorage on every render
  const [, forceRender] = useState(0);
  const isAuthenticated = !!localStorage.getItem(ACCESS_TOKEN_KEY);

  // Decode current user from stored JWT — no verification, display only
  const currentUser: AuthUser | null = (() => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (!token) return null;
    const decoded = decodeJwtPayload(token);
    if (!decoded?.payload) return null;
    // Payload is AES-encrypted on the server — we only have the outer JWT here.
    // The server embeds { userid, iEmployeeId, ispcode } in the encrypted payload.
    // For display purposes, read the cached user info stored at login time instead.
    const cached = localStorage.getItem('bms_current_user');
    if (!cached) return null;
    try { return JSON.parse(cached) as AuthUser; } catch { return null; }
  })();

  const [loginMutation] = useMutation(LOGIN_MUTATION);
  const [logoutMutation] = useMutation(LOGOUT_MUTATION);

  const login = useCallback(async (username: string, password: string, tenantCode: string) => {
    // Clear any stale cache from a previous user's session before loading new data
    await apolloClient.clearStore();

    const { data } = await loginMutation({ variables: { username, password, tenantCode } });
    const payload = data?.login;
    if (!payload) throw new Error('Login failed — no payload returned');

    localStorage.setItem(ACCESS_TOKEN_KEY, payload.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, payload.refreshToken);
    localStorage.setItem('bms_current_user', JSON.stringify(payload.user));

    forceRender(n => n + 1);
    navigate('/dashboard', { replace: true });
  }, [apolloClient, loginMutation, navigate]);

  const logout = useCallback(async () => {
    try {
      await logoutMutation();
    } catch {
      // best-effort — clear local state regardless of server response
    }
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem('bms_current_user');
    // Wipe the Apollo InMemoryCache so the next user never sees this user's data
    await apolloClient.clearStore();
    forceRender(n => n + 1);
    navigate('/login', { replace: true });
  }, [apolloClient, logoutMutation, navigate]);

  const getAccessToken = useCallback(
    () => localStorage.getItem(ACCESS_TOKEN_KEY),
    []
  );

  return { isAuthenticated, currentUser, login, logout, getAccessToken };
}
